import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazySection } from '../hooks/useLazySection';
import { Download } from 'lucide-react';
import { skuRoiApi, type SkuRoiDateGrain, type SkuRoiMonitorParams, type SkuRoiMonitorRow, type SkuRoiSection } from '../api/skuRoi';
import { MultiSelect } from '../components/MultiSelect';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { alignMarketingDateRange, getCurrentCalendarRange, MARKETING_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import Pagination from '../components/Pagination';
import { SortableHeader, type SortState } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

interface DimDef {
  key: string;
  label: string;
  kind?: 'image' | 'money';
}

interface MetricDef {
  key: string;
  label: string;
  fmt: (v: number) => string;
  group?: 'platform' | 'site';
  permKey?: FieldKey;
}

interface SectionDef {
  id: SkuRoiSection;
  title: string;
  subtitle: string;
  notice?: string;
  dims: DimDef[];
  metrics: MetricDef[];
  pageSize: number;
  localFilters?: boolean;
}

interface FilterState {
  one_level_channel: string;
  two_level_channel: string;
  three_level_channel: string;
  product_sku: string;
  platform_sku: string;
  landing_page_type: string;
  landing_page_detail: string;
  business_sku_up_at: string;
  busku_orig_price_newest: string;
  busku_sales_price_newest: string;
  campaign_name: string;
  ad_group_name: string;
  ad_name: string;
}

interface LocalFilterState {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
}

type FilterExclusions = Partial<Record<'one_level_channel' | 'two_level_channel' | 'three_level_channel' | 'landing_page_type' | 'landing_page_detail', boolean>>;
type LocalFilterExclusions = Partial<Record<'utm_source' | 'utm_medium', boolean>>;

interface AppliedTopState {
  startDate: string;
  endDate: string;
  dateGrain: SkuRoiDateGrain;
  filters: FilterState;
  exclusions: FilterExclusions;
}

type SkuRoiExportParams = Omit<SkuRoiMonitorParams, 'page' | 'size'>;

const DATE_GRAINS: Array<{ value: SkuRoiDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

const CHANNEL_DIMS: DimDef[] = [
  { key: 'date', label: '日期维度' },
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'three_level_channel', label: '三级渠道' },
];

const PRICE_DIMS: DimDef[] = [
  { key: 'business_sku_up_at', label: '首次上架日期' },
  { key: 'business_sku_up_month', label: '首次上架日期' },
  { key: 'busku_orig_price_newest', label: '最新吊牌价', kind: 'money' },
  { key: 'busku_sales_price_newest', label: '最新促销价', kind: 'money' },
];

const SKU_SPEND_DIMS: DimDef[] = [
  ...CHANNEL_DIMS,
  { key: 'web_product_sku', label: '商品SKU' },
  { key: 'web_business_sku', label: '平台SKU' },
  { key: 'image', label: '商品图片', kind: 'image' },
  { key: 'business_sku_up_at', label: '首次上架日期' },
  { key: 'busku_orig_price_newest', label: '最新吊牌价', kind: 'money' },
  { key: 'busku_sales_price_newest', label: '最新促销价', kind: 'money' },
];

const PAID_LANDING_DIMS: DimDef[] = [
  ...CHANNEL_DIMS,
  { key: 'landing_page_type', label: '渠道落地页分组' },
  { key: 'landing_page_detail', label: '渠道落地页' },
  { key: 'image', label: '商品图片', kind: 'image' },
  { key: 'business_sku_up_at', label: '首次上架日期' },
  { key: 'busku_orig_price_newest', label: '最新吊牌价', kind: 'money' },
  { key: 'busku_sales_price_newest', label: '最新促销价', kind: 'money' },
];

const TRAFFIC_DIMS: DimDef[] = [
  ...CHANNEL_DIMS,
  { key: 'utm_source', label: '来源' },
  { key: 'utm_medium', label: '媒介' },
  { key: 'utm_campaign', label: 'Campaign' },
  { key: 'landing_page_type', label: '渠道落地页分组' },
  { key: 'landing_page_detail', label: '渠道落地页' },
  { key: 'product_sku', label: '商品SKU' },
  { key: 'platform_sku', label: '平台SKU' },
  { key: 'image_with_dn', label: '商品图片', kind: 'image' },
  ...PRICE_DIMS,
];

const fmtNum = (v: number, d = 2) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtPct = (v: number) => (v == null ? '-' : `${(v * 100).toFixed(2)}%`);

const AD_SITE_METRICS: MetricDef[] = [
  { key: 'impression_cnt', label: '展示次数', fmt: fmtInt, group: 'platform' },
  { key: 'click_cnt', label: '点击数', fmt: fmtInt, group: 'platform' },
  { key: 'ctr', label: 'ctr', fmt: fmtPct, group: 'platform' },
  { key: 'cpc', label: 'cpc', fmt: fmtNum, group: 'platform', permKey: 'cpc' },
  { key: 'spend_amt', label: '花费', fmt: fmtNum, group: 'platform', permKey: 'spend' },
];

const SECTIONS: SectionDef[] = [
  {
    id: 'sku_spend',
    title: 'SKU整体花费成效',
    subtitle: '展示商品在渠道下的花费数据，以及同周期站内商品销售成效。',
    dims: SKU_SPEND_DIMS,
    pageSize: 50,
    metrics: [
      ...AD_SITE_METRICS,
      { key: 'pdp_session_cnt', label: '商详页会话数', fmt: fmtInt, group: 'site' },
      { key: 'pdp_view_cnt', label: '商详页浏览次数', fmt: fmtInt, group: 'site' },
      { key: 'sales_qty', label: '商品销量', fmt: fmtInt, group: 'site' },
      { key: 'sales_amt', label: '商品销售额', fmt: fmtNum, group: 'site', permKey: 'revenue' },
      { key: 'pdp_view_conv_rate', label: '浏览销量转化率', fmt: fmtPct, group: 'site' },
      { key: 'pdp_session_conv_rate', label: '会话转化率', fmt: fmtPct, group: 'site' },
      { key: 'roas', label: '商品ROI', fmt: fmtNum, group: 'site', permKey: 'roas' },
    ],
  },
  {
    id: 'paid_landing',
    title: '付费广告渠道落地页转化效率',
    subtitle: '付费广告渠道落地页(落地页+渠道)',
    notice: '依据整站末次渠道归因，看投放不同落地页，在站内的转化效率。所以此处的站内ROI与上面的账户ROI值会有差异。',
    dims: PAID_LANDING_DIMS,
    pageSize: 50,
    metrics: [
      ...AD_SITE_METRICS,
      { key: 'session_cnt', label: '会话数', fmt: fmtInt, group: 'site' },
      { key: 'pay_order_cnt', label: '支付订单数', fmt: fmtInt, group: 'site' },
      { key: 'net_sales_amt', label: '净销售额', fmt: fmtNum, group: 'site', permKey: 'revenue' },
      { key: 'site_roi', label: '站内ROI', fmt: fmtNum, group: 'site', permKey: 'roas' },
    ],
  },
  {
    id: 'landing_product',
    title: '渠道落地页-商品转化',
    subtitle: '渠道落地页-商品视角',
    notice: '依据整站末次渠道归因，看不同落地页内商品带来的流量、转化数据。',
    dims: TRAFFIC_DIMS,
    pageSize: 50,
    localFilters: true,
    metrics: [
      { key: 'pdp_view_cnt', label: '商详页浏览次数', fmt: fmtInt, group: 'site' },
      { key: 'sales_qty', label: '商品销量', fmt: fmtInt, group: 'site' },
      { key: 'sales_amt', label: '商品销售额', fmt: fmtNum, group: 'site', permKey: 'revenue' },
      { key: 'browse_sales_conv_rate', label: '浏览销量转化率', fmt: fmtPct, group: 'site' },
    ],
  },
  {
    id: 'all_landing',
    title: '所有渠道落地页转化效率（全部是站内数据，无花费）',
    subtitle: '渠道落地页-订单视角（渠道落地页）',
    notice: '依据整站末次渠道归因，看投放不同落地页，在站内的转化效率。',
    dims: TRAFFIC_DIMS.filter(d => d.key !== 'product_sku' && d.key !== 'platform_sku'),
    pageSize: 50,
    localFilters: true,
    metrics: [
      { key: 'session_cnt', label: '会话数', fmt: fmtInt, group: 'site' },
      { key: 'pay_order_cnt', label: '支付订单数', fmt: fmtInt, group: 'site' },
      { key: 'net_sales_amt', label: '净销售额', fmt: fmtNum, group: 'site', permKey: 'revenue' },
      { key: 'session_conv_rate', label: '会话转化率', fmt: fmtPct, group: 'site' },
    ],
  },
];

const EMPTY_FILTERS: FilterState = {
  one_level_channel: '',
  two_level_channel: '',
  three_level_channel: '',
  product_sku: '',
  platform_sku: '',
  landing_page_type: '',
  landing_page_detail: '',
  business_sku_up_at: '',
  busku_orig_price_newest: '',
  busku_sales_price_newest: '',
  campaign_name: '',
  ad_group_name: '',
  ad_name: '',
};

const EMPTY_FILTER_EXCLUSIONS: FilterExclusions = {};
const FILTER_EXCLUSION_KEYS: Array<keyof FilterExclusions> = ['one_level_channel', 'two_level_channel', 'three_level_channel', 'landing_page_type', 'landing_page_detail'];
const splitCsvValue = (value: string) => value.split(',').map(item => item.trim()).filter(Boolean);
const exclusionParam = (value: Record<string, boolean>) => Object.entries(value).filter(([, enabled]) => enabled).map(([key]) => key).join(',');
const normalizeFilterValue = (value: string) => splitCsvValue(value).sort().join(',');
const sameFilterState = (left: FilterState, right: FilterState) => (Object.keys(EMPTY_FILTERS) as Array<keyof FilterState>).every(key => normalizeFilterValue(left[key]) === normalizeFilterValue(right[key]));
const sameFilterExclusions = (left: FilterExclusions, right: FilterExclusions) => FILTER_EXCLUSION_KEYS.every(key => Boolean(left[key]) === Boolean(right[key]));

function initialAppliedTopState(): AppliedTopState {
  return {
    startDate: daysAgo(6),
    endDate: today(),
    dateGrain: 'week',
    filters: EMPTY_FILTERS,
    exclusions: EMPTY_FILTER_EXCLUSIONS,
  };
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function getPresetRange(preset: DatePreset, customStart: string, customEnd: string) {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

function cleanParams<T extends Record<string, unknown>>(params: T): T {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value != null) out[key] = value;
  });
  return out as T;
}

function renderDimValue(dim: DimDef, value: string) {
  if (!value) return '-';
  if (dim.kind === 'image') {
    return (
      <img
        src={value}
        alt=""
        loading="lazy"
        style={{ width: 58, height: 34, objectFit: 'contain', background: '#f5f7fb', borderRadius: 4 }}
      />
    );
  }
  if (dim.kind === 'money') {
    const num = Number(value);
    return Number.isFinite(num) ? fmtNum(num) : value;
  }
  return value;
}

function metricValue(row: SkuRoiMonitorRow | null, metric: MetricDef, userLevel: number) {
  if (!row) return '-';
  const raw = metric.fmt(row.metrics?.[metric.key] ?? 0);
  if (!metric.permKey) return raw;
  return sf(raw, metric.permKey, userLevel).value ?? '锁定';
}

function canSortMetric(metric: MetricDef, userLevel: number) {
  return !metric.permKey || sf('0', metric.permKey, userLevel).display === 'show';
}

function DimensionPicker({
  dims,
  selected,
  onChange,
}: {
  dims: DimDef[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  return (
    <div className="dimension-select-row" style={{ display: 'flex' }}>
      <span className="dimension-select-label">动态维度</span>
      <MultiSelect
        options={dims.map(dim => ({ value: dim.key, label: dim.label }))}
        value={dims.filter(dim => selected.has(dim.key)).map(dim => dim.key)}
        onChange={values => onChange(new Set(values))}
        countLabelFormatter={count => `已选 ${count} 项`}
        tagDisplay
        style={{ width: 420 }}
      />
    </div>
  );
}

function FilterInput({
  label,
  value,
  onChange,
  width = 160,
  placeholder = '模糊搜索',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  width?: number;
  placeholder?: string;
}) {
  return (
    <label className="data-filter-field" style={{ width, flex: '0 0 auto' }}>
      <span className="data-filter-label">{label}</span>
      <input
        className="form-input data-filter-input"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%' }}
      />
    </label>
  );
}

const SKU_ROI_LABEL_FIELDS = ['one_level_channel', 'two_level_channel', 'three_level_channel', 'landing_page_type', 'landing_page_detail'] as const;
const SKU_ROI_UTM_LABEL_FIELDS = ['utm_source', 'utm_medium'] as const;

function LabelFilterSelect({ label, field, value, onChange, exclude = false, onExcludeChange, width = 180 }: {
  label: string; field: typeof SKU_ROI_LABEL_FIELDS[number]; value: string; onChange: (value: string) => void; exclude?: boolean; onExcludeChange: (exclude: boolean) => void; width?: number;
}) {
  return (
    <label className="data-filter-field" style={{ minWidth: width }}>
      <span className="data-filter-label">{label}</span>
      <ReportLabelSelect dataset="sku_roi" field={field} pageFields={SKU_ROI_LABEL_FIELDS} includeAll value={value ? splitCsvValue(value) : ['全部']} onChange={next => onChange(next.includes('全部') ? '' : next.join(','))} exclude={exclude} onExcludeChange={onExcludeChange} paginated={field === 'landing_page_detail'} width={width} />
    </label>
  );
}

function LocalLabelFilterSelect({ label, field, value, onChange, exclude = false, onExcludeChange }: {
  label: string; field: typeof SKU_ROI_UTM_LABEL_FIELDS[number]; value: string; onChange: (value: string) => void; exclude?: boolean; onExcludeChange: (exclude: boolean) => void;
}) {
  return (
    <label className="data-filter-field" style={{ width: 240 }}>
      <span className="data-filter-label">{label}</span>
      <ReportLabelSelect dataset="sku_roi" field={field} pageFields={SKU_ROI_UTM_LABEL_FIELDS} includeAll value={value ? splitCsvValue(value) : ['全部']} onChange={next => onChange(next.includes('全部') ? '' : next.join(','))} exclude={exclude} onExcludeChange={onExcludeChange} paginated width="100%" />
    </label>
  );
}

function LocalFilters({ value, exclusions, onChange, onExclusionsChange }: { value: LocalFilterState; exclusions: LocalFilterExclusions; onChange: (next: LocalFilterState) => void; onExclusionsChange: (next: LocalFilterExclusions) => void }) {
  const set = (key: keyof LocalFilterState, nextValue: string) => onChange({ ...value, [key]: nextValue });
  return (
    <div className="data-filter-row compact" style={{ gap: 10, marginBottom: 0 }}>
      <LocalLabelFilterSelect label="来源" field="utm_source" value={value.utm_source} onChange={v => set('utm_source', v)} exclude={Boolean(exclusions.utm_source)} onExcludeChange={exclude => onExclusionsChange({ ...exclusions, utm_source: exclude })} />
      <LocalLabelFilterSelect label="媒介" field="utm_medium" value={value.utm_medium} onChange={v => set('utm_medium', v)} exclude={Boolean(exclusions.utm_medium)} onExcludeChange={exclude => onExclusionsChange({ ...exclusions, utm_medium: exclude })} />
      <FilterInput label="Campaign" value={value.utm_campaign} onChange={v => set('utm_campaign', v)} width={240} />
    </div>
  );
}

function SectionTable({
  section,
  baseParams,
  userLevel,
  queryVersion,
  hasPendingTopChanges,
  onQueryAll,
}: {
  section: SectionDef;
  baseParams: Omit<SkuRoiMonitorParams, 'section' | 'dimensions' | 'page' | 'size'> | null;
  userLevel: number;
  queryVersion: number;
  hasPendingTopChanges: boolean;
  onQueryAll: () => void;
}) {
  const [draftDims, setDraftDims] = useState<Set<string>>(() => new Set(section.dims.map(d => d.key)));
  const [appliedDims, setAppliedDims] = useState<Set<string>>(() => new Set(section.dims.map(d => d.key)));
  const [draftLocalFilters, setDraftLocalFilters] = useState<LocalFilterState>({ utm_source: '', utm_medium: '', utm_campaign: '' });
  const [appliedLocalFilters, setAppliedLocalFilters] = useState<LocalFilterState>({ utm_source: '', utm_medium: '', utm_campaign: '' });
  const [draftLocalExclusions, setDraftLocalExclusions] = useState<LocalFilterExclusions>({});
  const [appliedLocalExclusions, setAppliedLocalExclusions] = useState<LocalFilterExclusions>({});
  const [appliedTopQueryVersion, setAppliedTopQueryVersion] = useState(queryVersion);
  const [rows, setRows] = useState<SkuRoiMonitorRow[]>([]);
  const [summary, setSummary] = useState<SkuRoiMonitorRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(section.pageSize);
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [sectionQueryVersion, setSectionQueryVersion] = useState(0);
  const [exportQuery, setExportQuery] = useState<SkuRoiExportParams | null>(null);
  const [exportDims, setExportDims] = useState(() => section.dims);
  const [sort, setSort] = useState<SortState<string>>(null);
  const sectionViewport = useLazySection(Boolean(baseParams));
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);

  const activeDims = useMemo(() => section.dims.filter(dim => appliedDims.has(dim.key)), [appliedDims, section.dims]);
  const dimensionsParam = useMemo(() => activeDims.map(dim => dim.key).join(','), [activeDims]);
  const hasMetricGroups = section.metrics.some(m => m.group === 'platform') && section.metrics.some(m => m.group === 'site');
  const platformMetrics = section.metrics.filter(m => m.group === 'platform');
  const siteMetrics = section.metrics.filter(m => m.group !== 'platform');
  const currentExportParams = useMemo<SkuRoiExportParams | null>(() => {
    if (!baseParams) return null;
    return cleanParams({
      ...baseParams,
      section: section.id,
      dimensions: dimensionsParam,
      ...(sort ? { sort_by: sort.key, sort_dir: sort.direction } : {}),
      ...(section.localFilters ? { ...appliedLocalFilters, exclude_fields: [baseParams.exclude_fields, exclusionParam(appliedLocalExclusions)].filter(Boolean).join(',') } : {}),
    });
  }, [appliedLocalExclusions, appliedLocalFilters, baseParams, dimensionsParam, section.id, section.localFilters, sort]);

  const fetchData = useCallback(async (nextPage = 1) => {
    if (!currentExportParams) {
      setExportQuery(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await skuRoiApi.getMonitor({
        ...currentExportParams,
        page: nextPage,
        size: pageSize,
      });
      const data = res.data;
      setRows(data?.list ?? []);
      setSummary(data?.summary ?? null);
      setTotal(data?.total ?? 0);
      setPage(data?.page ?? nextPage);
      setTotalPage(data?.total_page ?? 1);
      setExportQuery(currentExportParams);
      setExportDims(activeDims);
    } catch (e) {
      setRows([]);
      setSummary(null);
      setTotal(0);
      setTotalPage(1);
      setExportQuery(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentExportParams, pageSize]);

  useEffect(() => {
    setPageSize(section.pageSize);
  }, [section.id, section.pageSize]);

  useEffect(() => {
    if (appliedTopQueryVersion === queryVersion) return;
    setAppliedDims(new Set(draftDims));
    setAppliedLocalFilters(draftLocalFilters);
    setAppliedLocalExclusions(draftLocalExclusions);
    setAppliedTopQueryVersion(queryVersion);
  }, [appliedTopQueryVersion, draftDims, draftLocalExclusions, draftLocalFilters, queryVersion]);

  useEffect(() => {
    if (appliedTopQueryVersion !== queryVersion || !sectionViewport.ready) return;
    setPage(1);
    void fetchData(1);
  }, [appliedTopQueryVersion, fetchData, queryVersion, sectionQueryVersion, sectionViewport.ready]);

  useEffect(() => {
    setSort(current => current && !activeDims.some(dim => dim.key === current.key) && !section.metrics.some(metric => metric.key === current.key)
      ? null
      : current);
  }, [activeDims, section.metrics]);

  const toggleSort = useCallback((key: string) => {
    setPage(1);
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);

  useMarketingViewSection('detail:' + section.id, {
    label: section.title, state: { draftDims: [...draftDims], draftLocalFilters, draftLocalExclusions },
    restore: value => { setDraftDims(new Set(value.draftDims)); setDraftLocalFilters(value.draftLocalFilters); setDraftLocalExclusions(value.draftLocalExclusions); },
    loading: loading,
  });

  const querySection = () => {
    setAppliedDims(new Set(draftDims));
    setAppliedLocalFilters(draftLocalFilters);
    setAppliedLocalExclusions(draftLocalExclusions);
    if (hasPendingTopChanges) onQueryAll();
    else setSectionQueryVersion(version => version + 1);
  };
  const resetSection = () => {
    const defaultDims = new Set(section.dims.map(dim => dim.key));
    const defaultFilters = { utm_source: '', utm_medium: '', utm_campaign: '' };
    setDraftDims(defaultDims); setAppliedDims(defaultDims);
    setDraftLocalFilters(defaultFilters); setAppliedLocalFilters(defaultFilters);
    setDraftLocalExclusions({}); setAppliedLocalExclusions({});
    setSectionQueryVersion(version => version + 1);
  };

  const renderMetricCells = (row: SkuRoiMonitorRow | null) => section.metrics.map(metric => (
    <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {metricValue(row, metric, userLevel)}
    </td>
  ));

  const handleExport = useCallback(async () => {
    if (!exportQuery) return;
    try {
      await enqueueReportExport({
        report_key: 'sku_roi_monitor',
        title: `SKU ROI-${section.title}`,
        file_name: `SKU ROI_${section.title}_${exportQuery.start_date}_${exportQuery.end_date}.csv`,
        brand_id: exportQuery.brand_id,
        site_id: exportQuery.site_id,
        params: exportQuery,
        columns: [
          ...exportDims.map(dim => ({ key: dim.key, label: dim.label })),
          ...section.metrics.map(metric => ({ key: metric.key, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '导出任务提交失败', 'error');
    }
  }, [enqueueReportExport, exportDims, exportQuery, section.metrics, section.title, showToast]);

  return (
    <div ref={sectionViewport.ref}>
    <section style={{
      marginTop: 18,
      background: 'var(--surface)',
      border: '1px solid var(--border2)',
      borderRadius: 'var(--radius-sm)',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(15,23,42,.04)',
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{section.title}</div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={handleExport}
            disabled={loading || !exportQuery}
            title="导出当前报表"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={14} strokeWidth={2} />
            导出
          </button>
        </div>
        <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>{section.subtitle}</div>
        {section.notice && <div style={{ marginTop: 10, fontSize: 12, color: '#e11d48' }}>{section.notice}</div>}
      </div>

      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, borderBottom: '1px solid var(--border2)' }}>
        {section.localFilters && <LocalFilters value={draftLocalFilters} exclusions={draftLocalExclusions} onChange={setDraftLocalFilters} onExclusionsChange={setDraftLocalExclusions} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <DimensionPicker dims={section.dims} selected={draftDims} onChange={next => {
            const selected = new Set(next);
            setDraftDims(selected);
            setAppliedDims(selected);
          }} />
          {section.localFilters && <DashboardQueryActions loading={loading} onQuery={querySection} onReset={resetSection} queryLabel="查询" />}
        </div>
      </div>

      {error && <div style={{ margin: '0 16px 12px', color: '#dc2626', fontSize: 13 }}>{error}</div>}

      <div className="table-wrap" style={{ position: 'relative', maxHeight: 560, overflow: 'auto' }}>
        <table style={{ minWidth: Math.max(980, activeDims.length * 120 + section.metrics.length * 108) }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
            {hasMetricGroups && (
              <tr>
                {activeDims.map(dim => (
                  <SortableHeader key={dim.key} rowSpan={2} sortKey={dim.key} sort={sort} onSort={toggleSort} style={{ whiteSpace: 'nowrap', background: 'var(--surface2)' }}>{dim.label}</SortableHeader>
                ))}
                <th colSpan={platformMetrics.length} style={{ textAlign: 'center', background: 'var(--surface2)' }}>平台数据</th>
                <th colSpan={siteMetrics.length} style={{ textAlign: 'center', background: 'var(--surface2)' }}>站内数据</th>
              </tr>
            )}
            <tr>
              {!hasMetricGroups && activeDims.map(dim => (
                <SortableHeader key={dim.key} sortKey={dim.key} sort={sort} onSort={toggleSort} style={{ whiteSpace: 'nowrap', background: 'var(--surface2)' }}>{dim.label}</SortableHeader>
              ))}
              {section.metrics.map(metric => (
                <SortableHeader key={metric.key} sortKey={metric.key} sort={sort} onSort={toggleSort} align="right" disabled={!canSortMetric(metric, userLevel)} style={{ whiteSpace: 'nowrap', background: 'var(--surface2)' }}><MetricDescriptionTooltip label={metric.label} /></SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary && (
              <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
                {activeDims.length > 0 && <td colSpan={activeDims.length} style={{ color: 'var(--text2)' }}>总计</td>}
                {renderMetricCells(summary)}
              </tr>
            )}
            {!sectionViewport.ready ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>滚动到该报表后加载</td></tr>
            ) : loading && rows.length === 0 ? (
              <tr><td colSpan={99}><TableLoadingCell /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>暂无数据</td></tr>
            ) : rows.map((row, idx) => (
              <tr key={`${section.id}-${idx}`}>
                {activeDims.map(dim => (
                  <td key={dim.key} style={dim.kind === 'image' ? { padding: '4px 8px', textAlign: 'center' } : { whiteSpace: 'nowrap' }}>
                    {renderDimValue(dim, row.dimensions?.[dim.key] ?? '')}
                  </td>
                ))}
                {renderMetricCells(row)}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && rows.length > 0 && <TableLoadingOverlay />}
      </div>
      {total > 0 && (
        <Pagination
          page={page}
          totalPages={totalPage}
          onChange={p => fetchData(p)}
          total={`共 ${total} 条`}
          pageSize={pageSize}
          pageSizeOptions={[20, 50, 100]}
          onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setPage(1); }}
          disabled={loading}
        />
      )}
    </section>
    </div>
  );
}

export default function SkuRoi() {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const currentSite = useMemo(() => sites.find(site => site.id === siteId) ?? null, [siteId, sites]);
  const isUSSite = currentSite && brand === 'zeelool' ? regionFromSiteCode(currentSite.code) === 'us' : false;

  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [dateGrain, setDateGrain] = useState<SkuRoiDateGrain>('week');
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [filterExclusions, setFilterExclusions] = useState<FilterExclusions>(EMPTY_FILTER_EXCLUSIONS);
  const [appliedTop, setAppliedTop] = useState<AppliedTopState>(() => initialAppliedTopState());
  const [queryVersion, setQueryVersion] = useState(0);
  const initialQueryKeyRef = useRef('');

  const dateRange = useMemo(() => alignMarketingDateRange(getPresetRange(datePreset, customStart, customEnd), dateGrain), [customEnd, customStart, dateGrain, datePreset]);
  const baseParams = useMemo(() => {
    if (!brandId || !siteId || !isUSSite) return null;
    return cleanParams({
      brand_id: brandId,
      site_id: siteId,
      start_date: appliedTop.startDate,
      end_date: appliedTop.endDate,
      date_grain: appliedTop.dateGrain,
      ...appliedTop.filters,
      exclude_fields: exclusionParam(appliedTop.exclusions),
    });
  }, [appliedTop, brandId, isUSSite, siteId]);
  const hasPendingTopChanges = dateRange.start !== appliedTop.startDate
    || dateRange.end !== appliedTop.endDate
    || dateGrain !== appliedTop.dateGrain
    || !sameFilterState(filters, appliedTop.filters)
    || !sameFilterExclusions(filterExclusions, appliedTop.exclusions);

  const setFilter = (key: keyof FilterState, value: string) => setFilters(prev => ({ ...prev, [key]: value }));
  const query = () => {
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain, filters, exclusions: filterExclusions });
    setQueryVersion(v => v + 1);
  };
  useMarketingViewSection('page', {
    label: '公共日期与渠道商品筛选', state: { datePreset, customStart, customEnd, dateGrain, filters, filterExclusions },
    restore: value => { setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); setDateGrain(value.dateGrain); setFilters(value.filters); setFilterExclusions(value.filterExclusions); },
    loading: false, query: () => { query(); },
    resolve: value => { const range = getPresetRange(value.datePreset, value.customStart, value.customEnd); return { ...value, datePreset: value.datePreset === 'today' ? 'custom' as const : value.datePreset, customStart: range.start, customEnd: range.end }; },
  });

  const reset = () => {
    setDateGrain('week');
    setDatePreset('7d');
    setCustomStart(daysAgo(7));
    setCustomEnd(today());
    setFilters(EMPTY_FILTERS);
    setFilterExclusions(EMPTY_FILTER_EXCLUSIONS);
    const range = getPresetRange('7d', daysAgo(7), today());
    setAppliedTop({ startDate: range.start, endDate: range.end, dateGrain: 'week', filters: EMPTY_FILTERS, exclusions: EMPTY_FILTER_EXCLUSIONS });
    setQueryVersion(v => v + 1);
  };
  useEffect(() => {
    if (!brandId || !siteId || !isUSSite) return;
    const key = `${brandId}:${siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain, filters, exclusions: filterExclusions });
    setQueryVersion(v => v + 1);
  }, [brandId, dateGrain, dateRange.end, dateRange.start, filterExclusions, filters, isUSSite, siteId]);

  if (!sitesLoaded) {
    return (
      <div className="page-section visible">
        <div className="card" style={{ padding: 0 }}><TableLoadingState /></div>
      </div>
    );
  }

  if (!isUSSite) {
    return (
      <div className="page-section visible">
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
          商品ROI监控看板仅支持 US 站点。
        </div>
      </div>
    );
  }

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <MarketingTimeRangeFilter grain={dateGrain} grains={DATE_GRAINS} onGrainChange={setDateGrain} preset={datePreset} presets={MARKETING_RANGE_PRESETS} onPresetChange={setDatePreset} start={customStart} end={customEnd} max={today()} customPreset="custom" onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
        </div>
      </div>

      <div className="data-filter-card">
        <div className="data-filter-row compact">
          <LabelFilterSelect label="一级渠道" field="one_level_channel" value={filters.one_level_channel} onChange={v => setFilter('one_level_channel', v)} exclude={Boolean(filterExclusions.one_level_channel)} onExcludeChange={exclude => setFilterExclusions(current => ({ ...current, one_level_channel: exclude }))} />
          <LabelFilterSelect label="二级渠道" field="two_level_channel" value={filters.two_level_channel} onChange={v => setFilter('two_level_channel', v)} exclude={Boolean(filterExclusions.two_level_channel)} onExcludeChange={exclude => setFilterExclusions(current => ({ ...current, two_level_channel: exclude }))} />
          <LabelFilterSelect label="三级渠道" field="three_level_channel" value={filters.three_level_channel} onChange={v => setFilter('three_level_channel', v)} exclude={Boolean(filterExclusions.three_level_channel)} onExcludeChange={exclude => setFilterExclusions(current => ({ ...current, three_level_channel: exclude }))} />
          <FilterInput label="商品SKU" value={filters.product_sku} onChange={v => setFilter('product_sku', v)} />
          <FilterInput label="平台SKU" value={filters.platform_sku} onChange={v => setFilter('platform_sku', v)} />
          <LabelFilterSelect label="渠道落地页分组" field="landing_page_type" value={filters.landing_page_type} onChange={v => setFilter('landing_page_type', v)} exclude={Boolean(filterExclusions.landing_page_type)} onExcludeChange={exclude => setFilterExclusions(current => ({ ...current, landing_page_type: exclude }))} />
          <LabelFilterSelect label="渠道落地页" field="landing_page_detail" value={filters.landing_page_detail} onChange={v => setFilter('landing_page_detail', v)} exclude={Boolean(filterExclusions.landing_page_detail)} onExcludeChange={exclude => setFilterExclusions(current => ({ ...current, landing_page_detail: exclude }))} />
          <FilterInput label="首次上架日期" value={filters.business_sku_up_at} onChange={v => setFilter('business_sku_up_at', v)} />
          <FilterInput label="最新吊牌价" value={filters.busku_orig_price_newest} onChange={v => setFilter('busku_orig_price_newest', v)} />
          <FilterInput label="最新促销价" value={filters.busku_sales_price_newest} onChange={v => setFilter('busku_sales_price_newest', v)} />
          <DashboardQueryActions onReset={reset} onQuery={query} />
        </div>
      </div>
      </div>

      {SECTIONS.map(section => (
        <SectionTable
          key={section.id}
          section={section}
          baseParams={baseParams}
          userLevel={userLevel}
          queryVersion={queryVersion}
          hasPendingTopChanges={hasPendingTopChanges}
          onQueryAll={query}
        />
      ))}
    </div>
  );
}
